/**
 * Group-thread shared message doc (group-threading P3, Option B).
 *
 * P3c stores group-thread messages in a top-level, membership-gated collection
 *   group_thread_messages/{threadId}/messages/{messageId}
 * rather than under the owner's user tree — so `users/{uid}/**` stays strictly
 * owner-only and a participant reads the shared thread via the participant-gated
 * rules (firestore.rules, added in P3c). This module is the PURE contract for
 * that doc: the shape plus the ChatMessage↔doc mappers, so the server write (P3b)
 * and the client read (P3c) agree, and it all unit-tests in isolation.
 *
 * The one thing this store adds over the per-user V2 doc: `senderId` is a
 * FIRST-CLASS, REQUIRED field — a group message is always attributed to the human
 * uid who sent it (or the 'assistant' sentinel for AI turns) so the render can
 * label "who". Everything else mirrors the per-message V2 conventions (orderTs +
 * seq ordering, ISO timestamp, schemaVersion) and reuses the existing base
 * mapper, so a group doc and a per-user doc revive to the same ChatMessage shape.
 *
 * Inert until P3b/P3c wire it: defining the contract writes/reads nothing.
 */
import type { ChatMessage } from '../components/GlobalChatSystem/types';
import { toMessageDoc, fromMessageDoc } from './chatThreadMessagesFirestore';
import { perDocMessageId, orderTsForMessage } from './chatThreadMessageDocFirestore';
import { canonicalGroupOrderTs } from './taskAnchor';

export const GROUP_THREAD_MESSAGE_SCHEMA_VERSION = 1 as const;

/** Sentinel senderId for AI/system turns (human turns carry the sender's Firebase uid). */
export const ASSISTANT_SENDER_ID = 'assistant' as const;

export interface GroupThreadMessageDoc extends Record<string, unknown> {
  /** Stable per-message key (also the Firestore doc id). */
  messageId: string;
  threadId: string;
  /** Uid of the human sender, or ASSISTANT_SENDER_ID for AI turns. Always set. */
  senderId: string;
  /** Epoch millis for chronological ordering (primary sort). */
  orderTs: number;
  /** Insertion-order tiebreaker within an equal orderTs (secondary sort). */
  seq: number;
  /** ISO timestamp of the message (revived to Date on read). */
  timestamp: string;
  schemaVersion: number;
}

/**
 * Resolve the senderId to stamp on a message. Assistant/system turns always map
 * to ASSISTANT_SENDER_ID; a human turn uses its own `senderId` if already set,
 * else the acting uid the caller supplies (the server has this per turn). Returns
 * '' only when a human turn's sender is genuinely unknown — callers (P3b) should
 * pass the authenticated acting uid so that never happens.
 */
export function resolveGroupSenderId(
  message: Pick<ChatMessage, 'isUser' | 'senderId'>,
  actingUid?: string | null,
): string {
  if (!message.isUser) return ASSISTANT_SENDER_ID;
  const explicit = (message.senderId ?? '').trim();
  if (explicit) return explicit;
  return (actingUid ?? '').trim();
}

/**
 * messageId for the shared store. Reuses the per-doc convergence anchor
 * (explicit id → task anchor → synthesized stable id). For a SYNTHESIZED id (an
 * id-less message), the sender is folded in so two different humans' otherwise
 * identical messages (same role + text + ts) don't collapse onto one doc in a
 * group thread — a case the per-user store never had to consider.
 */
function groupMessageId(message: ChatMessage, senderId: string): string {
  const base = perDocMessageId(message);
  return base.startsWith('synth_') && senderId ? `${base}__${senderId}` : base;
}

/**
 * Serialize a ChatMessage into a shared group-thread message doc. `senderId` is
 * the resolved sender (see resolveGroupSenderId); `seq` is the message's position
 * in the source array at write time (stable insertion-order tiebreaker).
 */
export function toGroupThreadMessageDoc(
  message: ChatMessage,
  threadId: string,
  senderId: string,
  seq: number,
): GroupThreadMessageDoc {
  const base = toMessageDoc(message) as Record<string, unknown>;
  const resolvedSender = (senderId ?? '').trim() || (message.isUser ? '' : ASSISTANT_SENDER_ID);
  const messageId = groupMessageId(message, resolvedSender);
  return {
    ...base,
    id: messageId,
    messageId,
    threadId,
    senderId: resolvedSender,
    orderTs: orderTsForMessage(message),
    seq,
    timestamp: typeof base.timestamp === 'string' ? base.timestamp : new Date(0).toISOString(),
    schemaVersion: GROUP_THREAD_MESSAGE_SCHEMA_VERSION,
  };
}

/** Revive one shared group-thread doc back into a ChatMessage (carrying senderId). */
export function fromGroupThreadMessageDoc(doc: Record<string, unknown>): ChatMessage {
  const message = fromMessageDoc(doc);
  const senderId = typeof doc.senderId === 'string' ? doc.senderId.trim() : '';
  // Server-resolved sender name (2026-08-26): the render path's floor when the
  // viewer's roster cannot name this uid. See ChatMessage.senderDisplayName.
  const senderDisplayName = typeof doc.senderDisplayName === 'string'
    ? doc.senderDisplayName.trim()
    : '';
  // Carry the shared-store sort key onto the message. Without it the merge that
  // unions own-tree and overlay has only `timestamp` to go on, and a turn whose
  // question and answer share a timestamp can render answer-first (see the
  // `orderTs` note on ChatMessage).
  const orderTs = typeof doc.orderTs === 'number' && Number.isFinite(doc.orderTs)
    ? doc.orderTs : undefined;
  const seq = typeof doc.seq === 'number' && Number.isFinite(doc.seq) ? doc.seq : undefined;
  return {
    ...message,
    ...(senderId ? { senderId } : {}),
    ...(senderDisplayName ? { senderDisplayName } : {}),
    ...(orderTs !== undefined ? { orderTs } : {}),
    ...(seq !== undefined ? { seq } : {}),
  };
}

/**
 * Deserialize + order a set of shared group-thread docs into a ChatMessage[].
 *
 * Sorted on the CANONICAL position (canonicalGroupOrderTs — the turn's mint
 * time recovered from the doc's own id, else its stored `orderTs`), then `seq`.
 * Reading the stamp straight off the doc rendered every question BELOW its own
 * answer for a turn whose two docs were stamped by writers with different
 * clocks (2026-09-09; see that helper's note). The writer is fixed, but the
 * docs already written keep their skewed stamps and nothing rewrites a
 * transcript — so the reader derives.
 */
export function fromGroupThreadMessageDocs(docs: GroupThreadMessageDoc[]): ChatMessage[] {
  if (!Array.isArray(docs) || docs.length === 0) return [];
  return docs
    .filter((d) => d && typeof d.messageId === 'string')
    .slice()
    .sort((a, b) => {
      const at = canonicalGroupOrderTs(a) ?? 0;
      const bt = canonicalGroupOrderTs(b) ?? 0;
      if (at !== bt) return at - bt;
      const as = typeof a.seq === 'number' ? a.seq : 0;
      const bs = typeof b.seq === 'number' ? b.seq : 0;
      return as - bs;
    })
    .map((d) => fromGroupThreadMessageDoc(d));
}
